import Loading from '../atoms/Loading';
import Text from '../base/Text';
import CrossIcon from '../icons/CrossIcon';
import Button from '../input/Button';

export default function UploadNotice({
  errorText,
  status,
  uploadPercent,
  processingPercent,
  handleClick,
  handleClose,
  type: kind,
}: {
  errorText: string;
  status: 'UPLOADING' | 'QUEUED' | 'PROCESSING' | 'SUCCESS' | 'ERROR';
  uploadPercent?: number;
  processingPercent?: number;
  handleClick: () => void;
  handleClose?: () => void;
  type:
    | 'characterGeneration'
    | 'characterFile'
    | 'motionGeneration'
    | 'videoProcessing';
}) {
  const effectiveKind:
    | 'characterGeneration'
    | 'characterFile'
    | 'motionGeneration'
    | 'videoProcessing' = kind;

  const fileLabel =
    effectiveKind === 'characterGeneration'
      ? 'Character generation'
      : effectiveKind === 'motionGeneration'
        ? 'Motion generation'
        : effectiveKind === 'videoProcessing'
          ? 'Video processing'
          : 'Character file';

  const successLabel =
    effectiveKind === 'characterGeneration'
      ? 'Character generation was successful'
      : effectiveKind === 'motionGeneration'
        ? 'Motion generation was successful'
        : effectiveKind === 'videoProcessing'
          ? 'Video processed successfully'
          : 'Upload was successful';

  return (
    <div
      className="shadow-menu text-gray-90 dark:bg-gray-90 pointer-events-auto mx-auto mt-4 flex min-h-12 w-fit max-w-lg cursor-pointer items-center justify-between gap-2 overflow-hidden rounded-2xl bg-white p-4 dark:text-white"
      onClick={handleClick}
    >
      <div className="flex items-center gap-3 pr-5">
        {status === 'ERROR' ? (
          <>
            <div className="text-red bg-red/30 dark:bg-red/20 flex h-8 w-8 flex-none items-center justify-center self-start rounded-lg">
              <svg
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.4917 18.5815C9.31852 18.5815 8.21452 18.3574 7.17969 17.9092C6.14486 17.4665 5.23177 16.8494 4.44043 16.0581C3.65462 15.2668 3.0376 14.3537 2.58936 13.3188C2.14111 12.284 1.91699 11.18 1.91699 10.0068C1.91699 8.83366 2.14111 7.72965 2.58936 6.69482C3.0376 5.65999 3.65462 4.74967 4.44043 3.96387C5.23177 3.17253 6.14209 2.55273 7.17139 2.10449C8.20622 1.65625 9.31022 1.43213 10.4834 1.43213C11.6621 1.43213 12.7689 1.65625 13.8037 2.10449C14.8385 2.55273 15.7516 3.17253 16.543 3.96387C17.3343 4.74967 17.9541 5.65999 18.4023 6.69482C18.8506 7.72965 19.0747 8.83366 19.0747 10.0068C19.0747 11.18 18.8506 12.284 18.4023 13.3188C17.9541 14.3537 17.3343 15.2668 16.543 16.0581C15.7516 16.8494 14.8385 17.4665 13.8037 17.9092C12.7689 18.3574 11.6649 18.5815 10.4917 18.5815ZM10.4917 16.8882C11.4491 16.8882 12.3428 16.7111 13.1729 16.3569C14.0085 16.0028 14.7389 15.5103 15.3643 14.8794C15.9951 14.2485 16.4876 13.5181 16.8418 12.688C17.196 11.8579 17.373 10.9642 17.373 10.0068C17.373 9.05501 17.196 8.16406 16.8418 7.33398C16.4876 6.49837 15.9924 5.76514 15.356 5.13428C14.7251 4.50342 13.9946 4.0109 13.1646 3.65674C12.3345 3.30257 11.4408 3.12549 10.4834 3.12549C9.53158 3.12549 8.63786 3.30257 7.80225 3.65674C6.97217 4.0109 6.24447 4.50342 5.61914 5.13428C4.99382 5.76514 4.50407 6.49837 4.1499 7.33398C3.79574 8.16406 3.61865 9.05501 3.61865 10.0068C3.61865 10.9642 3.79574 11.8579 4.1499 12.688C4.50407 13.5181 4.99382 14.2485 5.61914 14.8794C6.25 15.5103 6.98047 16.0028 7.81055 16.3569C8.64616 16.7111 9.53988 16.8882 10.4917 16.8882ZM10.4917 11.3848C10.0103 11.3848 9.76123 11.1413 9.74463 10.6543L9.62842 6.62012C9.61735 6.37663 9.69206 6.17741 9.85254 6.02246C10.013 5.86751 10.2205 5.79004 10.4751 5.79004C10.7297 5.79004 10.9399 5.87028 11.106 6.03076C11.272 6.18571 11.3494 6.38216 11.3384 6.62012L11.2056 10.6543C11.1945 11.1413 10.9565 11.3848 10.4917 11.3848ZM10.4917 14.1406C10.2205 14.1406 9.98812 14.0549 9.79443 13.8833C9.60628 13.7062 9.51221 13.4876 9.51221 13.2275C9.51221 12.9674 9.60628 12.7516 9.79443 12.5801C9.98812 12.403 10.2205 12.3145 10.4917 12.3145C10.7573 12.3145 10.9842 12.4002 11.1724 12.5718C11.366 12.7433 11.4629 12.9619 11.4629 13.2275C11.4629 13.4932 11.366 13.7118 11.1724 13.8833C10.9787 14.0549 10.7518 14.1406 10.4917 14.1406Z" />
              </svg>
            </div>
            <Text as="div" variant="studio/body/sm">
              {errorText}
            </Text>
          </>
        ) : status === 'SUCCESS' ? (
          <>
            <div className="text-green bg-green/30 flex h-8 w-8 flex-none items-center justify-center  self-start rounded-lg">
              <svg
                width="20"
                height="20"
                viewBox="0 0 15 15"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z"
                  fillRule="evenodd"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <Text as="div" variant="studio/body/sm">
              {successLabel}
            </Text>
          </>
        ) : (
          <>
            <div className="text-green bg-green/30 flex h-8 w-8 flex-none items-center justify-center  self-start rounded-lg">
              <Loading trackClassName="stroke-gray-30 opacity-100" />
            </div>
            <Text as="div" variant="studio/body/sm">
              {fileLabel}...{' '}
              {status === 'UPLOADING'
                ? 'Uploading'
                : status === 'QUEUED'
                  ? 'Queued'
                  : 'Processing'}{' '}
              <Percentage
                value={
                  status === 'UPLOADING' ? uploadPercent : processingPercent
                }
              />
            </Text>
          </>
        )}
      </div>
      <Button size="sm" className="flex-none">
        View
      </Button>
      {handleClose && (
        <Button
          variant="tertiary"
          size="sm"
          icon={<CrossIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        />
      )}
    </div>
  );
}

function Percentage({ value }: { value: number | undefined }) {
  if (!value) return <span className="inline-block w-12"></span>;
  return <span className="inline-block w-12">{value}%</span>;
}
